import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChat } from '../../contexts/ChatContext';
import { useToast } from '../../contexts/ToastContext';
import { apiRequest } from '../../services/api';
import ChatLayout from '../../components/Layout/ChatLayout';
import Sidebar from '../../components/Sidebar/Sidebar';
import { Eye, EyeSlash, GearSix } from '@phosphor-icons/react';

const TABS = [
  { id: 'profile', label: 'My Profile' },
  { id: 'password', label: 'Password' },
  { id: 'classes', label: 'My Classes' },
];

const PasswordInput = ({ value, onChange, placeholder, ...props }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <input
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500"
        {...props}
      />
      <button
        type="button"
        onClick={() => setVisible(!visible)}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        tabIndex={-1}
        aria-label={visible ? 'Hide password' : 'Show password'}
      >
        {visible ? <EyeSlash size={20} /> : <Eye size={20} />}
      </button>
    </div>
  );
};

const StudentSettingsPage = () => {
  const navigate = useNavigate();
  const {
    sessions,
    currentSessionId,
    setCurrentSessionId,
    deleteSession,
    clearAllSessions
  } = useChat();
  const [activeTab, setActiveTab] = useState('profile');

  const handleNewChat = () => {
    setCurrentSessionId(null);
    navigate('/student/chat');
  };

  return (
    <ChatLayout>
      <Sidebar
        sessions={sessions}
        currentSessionId={currentSessionId}
        onNewChat={handleNewChat}
        onSelectSession={(id) => {
          setCurrentSessionId(id);
          navigate('/student/chat');
        }}
        onDeleteSession={deleteSession}
        onClearAll={clearAllSessions}
      />
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <GearSix size={28} className="text-indigo-600" />
            Settings
          </h2>

          {/* Tab selector - modern underline style */}
          <div className="flex gap-6 border-b border-gray-200 mb-6">
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`pb-3 text-sm font-medium transition-colors relative ${
                  activeTab === tab.id
                    ? 'text-indigo-700 border-b-2 border-indigo-600'
                    : 'text-gray-500 hover:text-gray-900'
                }`}>
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'profile' && <ProfileTab />}
          {activeTab === 'password' && <PasswordTab />}
          {activeTab === 'classes' && <ClassesTab />}
        </div>
      </div>
    </ChatLayout>
  );
};

const ProfileTab = () => {
  const { showToast } = useToast();
  const [profile, setProfile] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await apiRequest('/api/student/profile', 'GET');
        setProfile(data);
        setDisplayName(data.student?.display_name || '');
      } catch {
        showToast('Failed to load profile', 'error');
      }
    };
    fetchProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiRequest('/api/student/profile', 'PUT', { display_name: displayName });
      showToast('Profile updated!', 'success');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!profile) {
    return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>;
  }

  // Avatar initials
  const initials = (profile.student?.display_name || profile.student?.username || 'S')
    .split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  const formatDate = (iso) => {
    if (!iso) return 'N/A';
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 border-t-4 border-t-indigo-500 p-6 max-w-lg">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">My Profile</h3>

      {/* Avatar */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-700 text-white ring-4 ring-indigo-100 flex items-center justify-center text-xl font-bold">
          {initials}
        </div>
        <div>
          <p className="font-medium text-gray-900">{profile.student?.display_name || profile.student?.username}</p>
          <p className="text-sm text-gray-500">{profile.school?.name}</p>
        </div>
      </div>

      <form onSubmit={handleSave} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
          <input type="text" value={profile.student?.username || ''} disabled
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500" />
          <p className="text-xs text-gray-400 mt-1">Username cannot be changed</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
          <input type="text" value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500"
            maxLength={200} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Grade Level</label>
            <input type="text" value={profile.student?.grade_level || ''} disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 capitalize" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">School</label>
            <input type="text" value={profile.school?.name || ''} disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Member Since</label>
            <input type="text" value={formatDate(profile.student?.created_at)} disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Login</label>
            <input type="text" value={formatDate(profile.student?.last_login_at)} disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500" />
          </div>
        </div>

        <button type="submit" disabled={saving || !displayName.trim()}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium">
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </form>
    </div>
  );
};

const PasswordTab = () => {
  const { showToast } = useToast();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      showToast('New passwords do not match', 'error');
      return;
    }
    if (newPassword.length < 6) {
      showToast('Password must be at least 6 characters', 'error');
      return;
    }

    setSaving(true);
    try {
      await apiRequest('/api/student/password', 'PUT', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      showToast('Password changed successfully!', 'success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Simple strength indicator
  const getStrength = () => {
    if (!newPassword) return null;
    if (newPassword.length < 6) return { label: 'Too short', color: 'red' };
    if (newPassword.length < 8) return { label: 'Fair', color: 'yellow' };
    if (/[A-Z]/.test(newPassword) && /[0-9]/.test(newPassword)) return { label: 'Strong', color: 'green' };
    return { label: 'Good', color: 'blue' };
  };
  const strength = getStrength();

  return (
    <div className="bg-white rounded-lg border border-gray-200 border-t-4 border-t-indigo-500 p-6 max-w-lg">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h3>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
          <PasswordInput value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
          <PasswordInput value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required minLength={6} />
          {strength && (
            <p className={`text-xs mt-1 ${{
              red: 'text-red-600',
              yellow: 'text-yellow-600',
              green: 'text-green-600',
              blue: 'text-blue-600',
            }[strength.color] || 'text-gray-600'}`}>
              Strength: {strength.label}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
          <PasswordInput value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required minLength={6} />
        </div>

        <button type="submit" disabled={saving || !currentPassword || !newPassword || !confirmPassword}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium">
          {saving ? 'Changing...' : 'Change Password'}
        </button>
      </form>
    </div>
  );
};

const ClassesTab = () => {
  const { showToast } = useToast();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joinCode, setJoinCode] = useState('');
  const [joining, setJoining] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const fetchClasses = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiRequest('/api/student/classes', 'GET');
      setClasses(data.classes || []);
    } catch {
      showToast('Failed to load classes', 'error');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  const handlePreview = async () => {
    if (!joinCode.trim()) return;
    setPreviewing(true);
    setPreview(null);
    try {
      const data = await apiRequest('/api/student/enroll/preview', 'POST', {
        activation_code: joinCode.trim(),
      });
      setPreview(data.class_section);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setPreviewing(false);
    }
  };

  const handleJoinClass = async (e) => {
    e.preventDefault();
    if (!joinCode.trim()) return;
    setJoining(true);
    try {
      const data = await apiRequest('/api/student/enroll', 'POST', {
        activation_code: joinCode.trim(),
      });
      showToast(`Joined ${data.class_section?.subject || 'class'} successfully!`, 'success');
      setJoinCode('');
      setPreview(null);
      fetchClasses();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="max-w-lg">
      <div className="bg-white rounded-lg border border-gray-200 border-t-4 border-t-indigo-500 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">My Classes</h3>

        {classes.length > 0 ? (
          <div className="space-y-3">
            {classes.map(cls => (
              <div key={cls.class_section_id} className="p-4 border border-gray-200 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-gray-900">{cls.subject}</p>
                    <p className="text-sm text-gray-600">
                      {cls.teacher_name}{cls.period_number ? ` \u2022 Period ${cls.period_number}` : ''}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">{cls.section_name}</p>
                  </div>
                  {cls.enrolled_at && (
                    <span className="text-xs text-gray-400">
                      Joined {new Date(cls.enrolled_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No classes yet. Use a code below to join one!</p>
        )}
      </div>

      {/* Join a class */}
      <div className="bg-white rounded-lg border border-gray-200 border-t-4 border-t-indigo-500 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Join Another Class</h3>

        <form onSubmit={handleJoinClass} className="space-y-3">
          <div className="flex gap-3">
            <input type="text" value={joinCode}
              onChange={(e) => { setJoinCode(e.target.value); setPreview(null); }}
              placeholder="Enter activation code"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 font-mono text-sm" />
            <button
              type="button"
              onClick={handlePreview}
              disabled={previewing || !joinCode.trim()}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 text-sm font-medium border border-gray-300"
            >
              {previewing ? 'Checking...' : 'Preview'}
            </button>
          </div>

          {/* Preview card */}
          {preview && (
            <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <p className="text-sm text-indigo-700 font-medium mb-1">You'll be joining:</p>
              <p className="font-semibold text-gray-900">
                {preview.subject}
                {preview.period_number ? ` (Period ${preview.period_number})` : ''}
              </p>
              <p className="text-sm text-gray-600">with {preview.teacher_name}</p>
              <button
                type="submit"
                disabled={joining}
                className="mt-3 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium"
              >
                {joining ? 'Joining...' : 'Confirm & Join'}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default StudentSettingsPage;
